import { Component, inject, Renderer2 } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { combineLatest, first } from "rxjs";
import { AsyncPipe, CurrencyPipe, NgForOf, NgIf, DOCUMENT } from "@angular/common";
import { LogService } from "../../../../services/log.service";
import { LabelType, NgxSliderModule, Options } from "@angular-slider/ngx-slider";
import { TranslocoPipe } from "@ngneat/transloco";
import {Store} from "@ngrx/store";
import {AppState} from "../../../../store/app.state";
import {
    selectMarketplaceAvailableFilters,
    selectMarketplaceFilters
} from "../../../../store/marketplace/marketplace.selectors";
import {setMarketplaceFilters, toggleMarketplaceShowFilters} from "../../../../store/marketplace/marketplace.actions";
import {MarketplaceFilters} from "../../../../store/marketplace/marketplace.state";

export interface SearchProductFilterForm {
    categories: FormArray<FormControl<boolean>>;
    goals: FormArray<FormControl<boolean>>;
    families: FormArray<FormControl<boolean>>;
    subscriptionRecurringPeriod: FormArray<FormControl<boolean>>;
    maxPrice: FormControl<[number, number]>;
}

@Component({
    selector: 'app-search-product-filters',
    standalone: true,
    imports: [
        ReactiveFormsModule,
        AsyncPipe,
        NgForOf,
        NgIf,
        NgxSliderModule,
        TranslocoPipe
    ],
    templateUrl: './search-product-filters.component.html',
    styleUrl: './search-product-filters.component.css'
})
export class SearchProductFiltersComponent {
    #logService = inject(LogService);
    // #componentStore = inject(MarketplacePageStore);
    #store = inject(Store<AppState>);
    #fb = inject(FormBuilder);

    private document = inject(DOCUMENT);
    private renderer = inject(Renderer2);

    availableFilters$ = this.#store.select(selectMarketplaceAvailableFilters);

    searchProductFilterForm?: FormGroup<SearchProductFilterForm>;
    priceRangeOptions: Options = {
        floor: 0,
        ceil: 0,
        translate: (value: number, label: LabelType): string => {
            return new CurrencyPipe('en-US', 'EUR').transform(value, 'EUR', 'symbol', '1.2-2') ?? '€ ' + value;
        }
    }

    constructor() {
        combineLatest([
            this.#store.select(selectMarketplaceAvailableFilters),
            this.#store.select(selectMarketplaceFilters)
        ]).pipe(first()).subscribe((values) => {
            const availableFilters = values[0];
            const filters = values[1];

            this.priceRangeOptions.ceil = availableFilters.maxPrice[1];

            this.searchProductFilterForm = this.#fb.nonNullable.group<SearchProductFilterForm>({
                categories: this.#fb.nonNullable.array<FormControl<boolean>>(availableFilters.categories.map(category => this.#fb.nonNullable.control(filters.categories.includes(category)))),
                goals: this.#fb.nonNullable.array<FormControl<boolean>>(availableFilters.goals.map(goal => this.#fb.nonNullable.control(filters.goals.includes(goal)))),
                families: this.#fb.nonNullable.array<FormControl<boolean>>(availableFilters.families.map(family => this.#fb.nonNullable.control(filters.families.includes(family)))),
                subscriptionRecurringPeriod: this.#fb.nonNullable.array<FormControl<boolean>>(availableFilters.subscriptionRecurringPeriod.map(period => this.#fb.nonNullable.control(filters.subscriptionRecurringPeriod.includes(period)))),
                maxPrice: this.#fb.nonNullable.control<[number, number]>([filters.maxPrice[0], filters.maxPrice[1] === 0 ? this.priceRangeOptions.ceil : filters.maxPrice[1]]),
            });
        });
    }

    toggleFilters() {
        this.#store.dispatch(toggleMarketplaceShowFilters());

        const body = this.document.body;
        if (body.classList.contains('overflow-hidden')) {
            this.renderer.removeClass(body, 'overflow-hidden');
        }
    }

    submitForm() {
        if (!this.searchProductFilterForm?.valid) {
            return;
        }
        const formValues = this.searchProductFilterForm.value;

        this.#store.select(selectMarketplaceAvailableFilters).pipe(first()).subscribe((availableFilters) => {
            const newFilters: MarketplaceFilters = {
                categories: availableFilters.categories.filter((c, i) => formValues.categories![i]),
                goals: availableFilters.goals.filter((g, i) => formValues.goals![i]),
                families: availableFilters.families.filter((f, i) => formValues.families![i]),
                subscriptionRecurringPeriod: availableFilters.subscriptionRecurringPeriod.filter((p, i) => formValues.subscriptionRecurringPeriod![i]),
                maxPrice: formValues.maxPrice ?? [0, 0]
            };
            this.#store.dispatch(setMarketplaceFilters({filters: newFilters}));
            this.toggleFilters();
        });
    }

    resetForm() {
        if (!this.searchProductFilterForm) {
            return;
        }

        const controls = this.searchProductFilterForm.controls;
        this.searchProductFilterForm.reset({
            categories: controls.categories.controls.map(() => false),
            goals: controls.goals.controls.map(() => false),
            families: controls.families.controls.map(() => false),
            subscriptionRecurringPeriod: controls.subscriptionRecurringPeriod.controls.map(() => false),
            maxPrice: this.getDefaultMaxPriceRange()
        });
    }

    resetCategories() {
        if (!this.searchProductFilterForm) {
            return;
        }

        const controls = this.searchProductFilterForm.controls;
        controls.categories.reset(controls.categories.controls.map(() => false));
    }

    resetFamilies() {
        if (!this.searchProductFilterForm) {
            return;
        }

        const controls = this.searchProductFilterForm.controls;
        controls.families.reset(controls.families.controls.map(() => false));
    }

    resetGoals() {
        if (!this.searchProductFilterForm) {
            return;
        }

        const controls = this.searchProductFilterForm.controls;
        controls.goals.reset(controls.goals.controls.map(() => false));
    }

    resetSubscriptionRecurringPeriod() {
        if (!this.searchProductFilterForm) {
            return;
        }

        const controls = this.searchProductFilterForm.controls;
        controls.subscriptionRecurringPeriod.reset(controls.subscriptionRecurringPeriod.controls.map(() => false));
    }

    resetMaxPrice() {
        if (!this.searchProductFilterForm) {
            return;
        }

        this.searchProductFilterForm.controls.maxPrice.reset(this.getDefaultMaxPriceRange());
    }

    getCategoryLabel(category: string): string {
        return category === 'bundle' ? 'Plus' : category;
    }

    private getDefaultMaxPriceRange(): [number, number] {
        return [0, this.priceRangeOptions.ceil ?? 0];
    }
}
